import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Sun, Moon, Monitor, Globe, Home } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/Button';
import { useLanguageRouter } from '../utils/i18nRouter';
import { EnhancedSearchInput } from './EnhancedSearchInput';

const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
  { code: 'zh', name: '中文', flag: '🇨🇳' },
  { code: 'zh-TW', name: '繁體中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Português', flag: '🇵🇹' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
  { code: 'th', name: 'ไทย', flag: '🇹🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
  { code: 'pl', name: 'Polski', flag: '🇵🇱' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪' },
];

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  showSearch?: boolean;
}

export const Header: React.FC<HeaderProps> = ({ searchQuery = '', onSearchChange, showSearch = false }) => {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();
  const { changeLanguage, currentLanguage, getLocalizedPath } = useLanguageRouter();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);


  const [isLangOpen, setIsLangOpen] = useState(false);

  // Check if we're on a tool page
  const isToolPage = location.pathname.includes('/tools/');
  const [isThemeOpen, setIsThemeOpen] = useState(false);

  const currentLang = languages.find(lang => lang.code === currentLanguage) || languages[0];

  const themeOptions = [
    { value: 'light', label: t('common.light'), icon: Sun },
    { value: 'dark', label: t('common.dark'), icon: Moon },
    { value: 'auto', label: t('common.auto'), icon: Monitor },
  ];

  const currentTheme = themeOptions.find(option => option.value === theme) || themeOptions[0];
  const ThemeIcon = currentTheme.icon;

  return (
    <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-gray-200 dark:bg-gray-900/80 dark:border-gray-700">
      <div className="px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Search or Home button */}
          <div className="flex-1 max-w-md">
            {showSearch ? (
              <EnhancedSearchInput
                searchQuery={searchQuery}
                onSearchChange={onSearchChange || (() => {})}
                placeholder={t('common.search')}
                className="max-w-md"
              />
            ) : isToolPage ? (
              <Link to={getLocalizedPath('/')}>
                <Button variant="ghost" size="sm" className="flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  <Home className="w-4 h-4" />
                  <span>{t('common.backToHome')}</span>
                </Button>
              </Link>
            ) : null}
          </div>

          {/* Desktop Actions */}
          <div className="flex items-center space-x-2">
            {/* Language Selector */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsLangOpen(!isLangOpen)}
                className="flex items-center space-x-1"
              >
                <Globe className="w-4 h-4" />
                <span className="text-sm">{currentLang.flag}</span>
              </Button>
              {isLangOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 max-h-64 overflow-y-auto">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code);
                        setIsLangOpen(false);
                      }}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <span>{lang.flag}</span>
                      <span className="text-sm">{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Theme Toggle */}
            <div className="relative">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsThemeOpen(!isThemeOpen)}
                className="flex items-center space-x-1"
              >
                <ThemeIcon className="w-4 h-4" />
                <span className="text-sm hidden sm:inline">{currentTheme.label}</span>
              </Button>
              {isThemeOpen && (
                <div className="absolute right-0 mt-2 w-32 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                  {themeOptions.map((option) => {
                    const OptionIcon = option.icon;
                    return (
                      <button
                        key={option.value}
                        onClick={() => {
                          setTheme(option.value as 'light' | 'dark' | 'auto');
                          setIsThemeOpen(false);
                        }}
                        className={`w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 ${
                          theme === option.value ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`}
                      >
                        <OptionIcon className="w-4 h-4" />
                        <span className="text-sm">{option.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="sm"
            className="md:hidden"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="md:hidden py-4 border-t border-gray-200 dark:border-gray-700">
            {/* Mobile Actions */}
            <div className="space-y-4">
              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">
                  {t('common.language')}:
                </span>
                <select
                  value={currentLanguage}
                  onChange={(e) => changeLanguage(e.target.value)}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600"
                >
                  {languages.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.flag} {lang.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <span className="text-sm text-gray-600 dark:text-gray-400 block mb-2">
                  {t('common.theme')}:
                </span>
                <select
                  value={theme}
                  onChange={(e) => setTheme(e.target.value as 'light' | 'dark' | 'auto')}
                  className="w-full text-sm border border-gray-300 rounded px-3 py-2 dark:bg-gray-800 dark:border-gray-600"
                >
                  {themeOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
